package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetPendingMembershipsUseCase
import com.rainbowclubs.rainbow.domain.use_cases.membership.UpdateMembershipStatusUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class PendingInvitationsViewModel(
    private val clubId: String,
    private val getPendingMembershipsUseCase: GetPendingMembershipsUseCase,
    private val updateMembershipStatusUseCase: UpdateMembershipStatusUseCase,
) : ViewModel() {

    sealed class NavigationEvent {
        object Back : NavigationEvent()
    }

    private val _screenState =
        MutableStateFlow<ScreenState<List<Membership>>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<List<Membership>>> = _screenState.asStateFlow()

    private val _actionLoadingState = MutableStateFlow<Pair<String, MembershipStatus>?>(null)
    val actionLoadingState: StateFlow<Pair<String, MembershipStatus>?> =
        _actionLoadingState.asStateFlow()

    private val _showActionError = MutableStateFlow(false)
    val showActionError: StateFlow<Boolean> = _showActionError.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchPendingInvitations()
    }

    fun fetchPendingInvitations() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getPendingMembershipsUseCase(clubId)
                .onSuccess {
                    _screenState.value = ScreenState.Content(it)
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }

    fun acceptInvitation(membershipId: String) {
        updateInvitationStatus(membershipId, MembershipStatus.APPROVED)
    }

    fun declineInvitation(membershipId: String) {
        updateInvitationStatus(membershipId, MembershipStatus.BLOCKED)
    }

    fun dismissActionError() {
        _showActionError.value = false
    }

    fun onBackClick() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    private fun updateInvitationStatus(
        membershipId: String,
        status: MembershipStatus,
    ) {
        viewModelScope.launch {
            _showActionError.value = false
            _actionLoadingState.value = membershipId to status
            updateMembershipStatusUseCase(membershipId, status)
                .onSuccess {
                    val currentState = _screenState.value
                    if (currentState is ScreenState.Content) {
                        _screenState.value = ScreenState.Content(
                            currentState.data.filterNot { membership ->
                                membership.membershipId == membershipId
                            },
                        )
                    }
                }
                .onFailure {
                    _showActionError.value = true
                }
            _actionLoadingState.value = null
        }
    }
}
